
import JigTextField from '@/components/input/JigTextField.vue'
import { UtilsConst } from '@/constants'
import IconSearch from '@svg/IconSearch.svg'
import { Component, Vue } from 'vue-property-decorator'

/**
 * The SearchField component provides a search input field with the following functionality:
 * - Real-time search as user types (via @input event)
 * - Committed search when user presses Enter or clicks search icon (via @keydown.enter and @click:prepend-inner events)
 * - Clear search functionality
 *
 * Events emitted:
 * - 'onSearch': Fires on every keystroke as user types - typically used for pure front-end filtering
 * - 'onSearchChange': Fires only when user commits the search (Enter key or search icon click) - typically used for API calls
 * - 'onSearchReset': Fires when user clears the search field
 *
 * Notes:
 * - searchItems() fires immediately on input change, useful for instant client-side filtering
 * - onSearchChange() fires only on user action (Enter/click), useful for debounced API searches to reduce server load
 */
@Component({
  components: {
    IconSearch,
    'jig-text-field': JigTextField,
  },
})
export default class SearchFieldVue extends Vue {
  private searchCriteria: string = ''

  /**
   * Emits search event based on the provided key.
   * Default behavior (key='onSearch'): Fires on every keystroke, used for real-time front-end filtering.
   * This is triggered by the @input event and executes immediately as the user types.
   *
   * @param key - The event key to emit, defaults to 'onSearch'
   */
  private searchItems(key: string = 'onSearch') {
    this.$emit(UtilsConst.searchEvents[key], this.searchCriteria)
  }

  /**
   * Handles committed search actions (Enter key press or search icon click).
   * Emits 'onSearchChange' event, typically used for API calls or backend searches.
   * This fires only when the user explicitly commits the search, reducing unnecessary API calls.
   */
  private onSearchChange() {
    this.searchItems('onSearchChange')
  }

  /**
   * Resets the search field and emits the reset event.
   * Triggered when user clicks the clear button.
   */
  private resetItems() {
    this.$emit(UtilsConst.searchEvents.onSearchReset)
  }
}
